import React, { useState, useContext, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";
import { 
	Button, 
	Modal, 
	Form, 
	Select, 
	Input, 
	InputNumber, 
	message, 
	DatePicker, 
	Divider, 
	Space 
} from "antd";
import { currencyGetter } from "../../../../utils/BankCurrencyUtils";
import { EntitiesContext, BankAccountContext } from "../../../../contexts";
import { CircleAnimationCompact } from "../../../loading";
import { PlusOutlined } from "@ant-design/icons";
import { extractDepthClassifications, formatCurrency, TransactionsType } from "../../utils";
import { ClassificationModal } from "./ClassificationModal";
import { ClassificationsContext } from "../../../../contexts/ClassificationsContext";

export const CreateTransactionModal = forwardRef(({
	token,	
	onCreateTransaction = () => {},
	loading = false,	
}, ref) => {
	const modalClassificationRef = useRef(null);

	const [ form ] = Form.useForm();
	const [ submittable, setSubmittable ] = useState(false);
	const { 
		position: positionFromForm = "in", 
		client_id: clientFromForm = null, 
		supplier_id: suuplierFromFrom = null 
	} = form.getFieldsValue();

	const { clients, suppliers } = useContext(EntitiesContext);	
	const { bankAccountSelected } = useContext(BankAccountContext);
	const { classifications } = useContext(ClassificationsContext);
	const options = extractDepthClassifications(classifications);

	const [openCreateTransaction, setOpenCreateTransaction] = useState(false);

	useImperativeHandle(ref, () => ({
		openModal: (value) => setOpenCreateTransaction(value),
	}));
	
	useEffect(() => {
		form
			.validateFields({
				validateOnly: true,
			})
			.then(() => setSubmittable(true))
			.catch(() => setSubmittable(false));
	}, [openCreateTransaction, form, Form.useWatch([], form)]);
	
	const handleFormChange = () => {
		form
			.validateFields()
			.then(() => setSubmittable(true))
			.catch(() => setSubmittable(false));
	};

	const createNewTransaction = async() => {
		const transactionFormValues = form.getFieldsValue();
		onCreateTransaction(transactionFormValues);
		form.resetFields();
	};
		
	const handlePositionChange = (value) => {
		const updates = { position: value };

		if (value === "in" && suuplierFromFrom) {
			updates.supplier_id = null; // Limpiar proveedor
			message.info("Proveedor removido, asigna un Cliente.");
		} else if (value === "out" && clientFromForm) {
			updates.client_id = null; // Limpiar cliente
			message.info("Cliente removido, asigna un Proveedor.");
		}

		form.setFieldsValue(updates);
		handleFormChange(); // Actualizar validez del formulario
	};

	const CreationView = (<>
		<br/>
		<hr/>								
		<p style={{fontSize: "17px"}}><strong>Salario Cuenta Bancaria: </strong>{currencyGetter[bankAccountSelected?.currency]} {formatCurrency(bankAccountSelected?.balance)}</p>
		<hr/>
		<br/>

		<Form 
			form={form} 
			name="transactionForm"
			layout="center"
			autoComplete="off"
			initialValues={{ position: "in", classification: "", client_id: null, supplier_id: null }}
		>		
			<Form.Item
				name="date"
				label="Fecha"
				rules={[{ required: true, message: "Porfavor ingresa una fecha." }]}
			>
				<DatePicker
					size="small"
					style={{
						width: "100%",
						borderRadius: 7
					}}
				/>
			</Form.Item>

			<Form.Item
				name="transaction_number"
				label="Número de Transacción"
				rules={[{ required: true, message: "Porfavor ingresa el Número de Transacción." }]}
			>
				<Input 
					onChange={(e) => {
						const value = e.target.value;						
						const numericValue = value.replace(/[^0-9]/g, "");
						e.target.value = numericValue;
					}}
				/>
			</Form.Item>

			<Form.Item
				name="description"
				label="Descripción"
				rules={[{ required: true, message: "Porfavor ingresa una descripción."  }]}
			>
				<Input 
					showCount
				/>
			</Form.Item>

			<Form.Item
				name="position"
				label="Posición"
				rules={[{ required: true, message: "Porfavor selecciona el tipo de posición a manejar."  }]}
			>
				<Select onChange={handlePositionChange}>
					<Select.Option value="in">Crédito</Select.Option>
					<Select.Option value="out">Débito</Select.Option>
				</Select>
			</Form.Item>

			<Form.Item
				name="amount"
				label="Monto"
				rules={[{ required: true, message: "Porfavor ingresa un monto." }]}
			>
				<InputNumber
					prefix={`${currencyGetter[bankAccountSelected?.currency]} ${positionFromForm === "in" ? "+" : "-"}`}
					style={{width: "100%"}}
					min={0.01}
					precision={2}
					step={0.01}
					controls={false}
				/>
			</Form.Item>

			<Form.Item
				name="amount_reference"
				label="Saldo de Referencia"
				rules={[{ required: true, message: "Porfavor ingresa un saldo de referencia." }]}
			>
				<InputNumber
					prefix={`${currencyGetter[bankAccountSelected?.currency]}`}
					style={{width: "100%"}}
					precision={2}
					step={0.01}
					controls={false}
				/>
			</Form.Item>

			<Form.Item
				name="transaction_type"
				label="Tipo de Transacción"
				rules={[{ required: true, message: "Porfavor selecciona un tipo de transacción específico."  }]}
			>
				<Select>
					{Object.entries(TransactionsType).map(([key, value]) => (
						<Select.Option key={key} value={key}>{value}</Select.Option>
					))}
				</Select>
			</Form.Item>


			<hr/>
			<br/>

			<Form.Item
				name="classification_id"
				label="Clasificación"				
			>
				<Select
					showSearch
					allowClear
					placeholder="Selecciona clasificación"
					style={{ width: "100%" }}
					options={options}										
					optionFilterProp="label"
					dropdownRender={(menu) => (
						<>
							{menu}
							<Divider
								style={{
									margin: "8px 0",
								}}
							/>
							<Space
								style={{
									padding: "0 8px 4px",
									width: "100%",
									display: "flex",
									justifyContent: "center",
								}}
							>
								<Button
									type="text"
									icon={<PlusOutlined />}
									style={{ color: "#2d5db7" }}
									onClick={modalClassificationRef.current?.openModal}
								>Agregar Nueva Clasificación</Button>
							</Space>
						</>
					)}
				/>
			</Form.Item>

			{positionFromForm === "in" && <Form.Item
				name="client_id"
				label="Cliente"
			>
				<Select>
					<Select.Option value={null}>Cliente Vacio</Select.Option>
					{clients.map((client) => (<Select.Option key={client.id} value={client.id}>{client.name}</Select.Option>))}
				</Select>
			</Form.Item>}

			{positionFromForm === "out" && <Form.Item
				name="supplier_id"
				label="Proveedor"					
			>
				<Select>
					<Select.Option value={null}> Proveedor Vacio</Select.Option>
					{suppliers.map((supplier) => (<Select.Option key={supplier.id} value={supplier.id}>{supplier.name}</Select.Option>))}
				</Select>
			</Form.Item>}

			<Form.Item
				name="note"
				label="Nota"
			>
				<Input 
					showCount
					maxLength={25}
				/>
			</Form.Item>
			<hr/>
		</Form>		
	</>);
	
	return (
		<Modal
			open={openCreateTransaction}
			centered
			width='40vw'
			title='Crear Nueva Transacción'			
			onCancel={() => {
				setOpenCreateTransaction(false);
			}}
			footer={() => (
				<>
					<Button
						onClick={() => setOpenCreateTransaction(false)}
						loading={loading}
					>
						Cancelar
					</Button>
					<Button
						type="primary"
						disabled={!submittable}
						onClick={createNewTransaction}
						loading={loading}
					>
						Guardar Transacción
					</Button>
				</>
			)}
		>
			{ loading ? <CircleAnimationCompact/> : CreationView }			

			<ClassificationModal
				ref={modalClassificationRef}
				token={token}				
			/>
		</Modal>
	);
});

CreateTransactionModal.displayName = "CreateTransactionModal";

CreateTransactionModal.propTypes = {
	token: PropTypes.string.isRequired,
	onCreateTransaction: PropTypes.func,
	loading: PropTypes.bool,
};
